import create from 'zustand';
import { Api, Subscription, subscription } from './api';

import { ChangeReason, EditAction } from './domain';
import { FormApi } from './form';
import { History, StateHistory } from './history';
import { ListApi } from './list';
import { VisitCode } from './visit-context';

// is action type necessary ?
export type ActionType = 'UNDO' | 'REDO' | 'JUMP';

// can history per object be instantiated without being invasive ?
export type Action<T = any> = {
  reason: ChangeReason;
  type: ActionType;
  value?: T;
  label?: string; // domain specifc label
};

type ChangeConfiguration<T = any> = {
  // api consumers responsible for current value and normalizing history
  value: T;
  changes?: Action<T>[];
  history?: History;
  changeLabel?: (value: T | T[]) => string;
};

type ChangeData<T = any> = {
  value: T;
  action: Action<T>;
  changes: Action[];
  openPrompt: boolean;
  canRedo: boolean;
  canUndo: boolean;
};

//One prompt for each type and field history
type ChangePromptOptions<T = any> = {
  title: string;
  action: EditAction;
  changeReasons: VisitCode[];

  updateDomain: (a: Action) => T; // raw value is updated and returned to history management, ie. set Audit, service date and return CPT
  noReview?: () => void;

  changeReasonRequired?: boolean;
  reviseCode?: boolean;
};

type ChangeActions<T = any> = {
  undo(): void;
  redo(): void;
  jump(ref: T): void;
  jumpIndex(i: number): void;
  update(): void;
  changeLabel: (v: T) => string;
};

type ChangeEvents = Subscription<ChangeData>;

type ChangeForms = {
  review: FormApi;
};

export type ChangeApi = Api<
  ChangeConfiguration,
  ChangeData,
  ChangeActions,
  ChangeForms,
  ChangeEvents
>;

/* 
Each element type instantiants the api with its own custom config.
Raw value mutations are wrapped by actions which provide a normalied structure for history management.
Custom encoding / decoding hooks allow different backend integrations
*/
export function changeApi(configuration: ChangeConfiguration): ChangeApi {
  const { value, changes, changeLabel } = configuration;

  const data = create<ChangeData>(() => ({
    value: value,
    action: null,
    changes: changes || [],
    openPrompt: false,
    canRedo: false,
    canUndo: false,
  }));

  const events = subscription<ChangeData>(
    ['action', 'value', 'changes', 'openPrompt', 'canRedo', 'canUndo'],
    data
  );

  const history =
    configuration.history ||
    new StateHistory({
      value,
      undoStack: changes || [],
    });

  function update() {
    data.setState({
      value: history.value,
      canUndo: history.canUndo,
      canRedo: history.canRedo,
      changes: history.changes(),
    });
  }

  let actions: ChangeActions;

  actions = {
    update,
    undo: () => {
      history.undo();
      update();
    },
    redo: () => {
      history.redo();
      update();
    },
    jump: (a) => {
      history.jump(a);
      update();
    },
    jumpIndex: (i) => {
      history.jumpIndex(i);
      update();
    },
    changeLabel: changeLabel || ((v: any) => (v || []).toString()),
  };

  return {
    configuration,
    data,
    actions,
    events,
  };
}

export type CollectionChangeApi<T = any> = ChangeApi & {
  itemChanges: () => [string, T[]][];
  itemHistory(item: T): History<T>;
  update(item?: T, action?: EditAction): void;
};
type CollectionConf<T> = Partial<ChangeConfiguration<T>> & {
  onUpdate?: (item: T, action?: EditAction) => void;
};

export function collectionChanges<T = any>(
  api: ListApi,
  conf?: CollectionConf<T>
): CollectionChangeApi<T> {
  const { id } = api.configuration;

  const history = new StateHistory({
    value: api.data.getState().all || [],
    onNavigate: (v: any[]) => {
      v = v || [];
      api.actions.setValuesDestructive(v);
    },
  });

  const actionHistory = new StateHistory();
  actionHistory.value = ['Empty', history.value];

  // TODO init existing item histories
  const itemHistories: Map<string, History<T>> = new Map();
  function itemHistory(item: T): History<T> {
    const key = id(item);
    if (!key) {
      throw 'No id for collection history item';
    }
    return (
      itemHistories.get(key) ||
      itemHistories.set(key, new StateHistory<T>()).get(key)
    );
  }

  const { onUpdate } = conf;

  function update(item?: T, action?: EditAction): void {
    history.value = api.data.getState().all || [];

    if (item) {
      actionHistory.value = [`${action} ${id(item)}`, history.value];

      if (onUpdate) {
        onUpdate(item, action);
      }

      const h = itemHistory(item);
      h.value = item;
    } else {
      return;
    }
    changes.actions.update();
    //  console.log('update after', history.changes(), 'action', actionHistory.changes())
  }

  api.events.added((item: T) => {
    update(item, 'CREATE');
  });
  api.events.removed((item: T) => {
    update(item, 'DELETE');
  });
  api.events.moved((item: T) => {
    update(item, 'MOVE');
  });
  api.events.edited((item: T) => {
    update(item, 'UPDATE');
  });

  const changes = changeApi({
    ...conf,
    ...{
      history,
      value: history.value,
    },
  });

  return {
    ...changes,
    ...{ itemHistory, update, itemChanges: () => actionHistory.changes() },
  };
}
